import type { ReactNode } from 'react';
import { symptomCategories } from '@/mocks/questionnaire';
import type { ConsultationRow } from '@/pages/admin/types';
import { levelOf } from '@/pages/admin/utils/level';
import {
  basicSections,
  fieldText,
  formatTime,
  historySectionList,
  moreSectionList,
  reportSectionList,
  optionText,
} from '@/pages/admin/utils/fields';
import type { FieldConfig } from '@/mocks/questionnaire';

interface ConsultationDetailProps {
  row: ConsultationRow | null;
  onClose: () => void;
}

const symptomBadge = (level: string | null | undefined) => {
  if (!level) return 'bg-background-100 text-foreground-400';
  if (level === 'severe') return 'bg-primary-500 text-background-50';
  if (level === 'moderate') return 'bg-accent-200 text-accent-900';
  if (level === 'mild') return 'bg-primary-100 text-primary-700';
  return 'bg-background-100 text-foreground-500';
};

function ValueRows({ row, fields }: { row: ConsultationRow; fields: FieldConfig[] }) {
  const asRecord = row as unknown as Record<string, unknown>;
  const filled = fields
    .map((field) => ({ label: field.label, value: fieldText(asRecord, field.name) }))
    .filter((item) => item.value);

  if (!filled.length) {
    return <p className="text-xs text-foreground-400">这一部分没有填写</p>;
  }

  return (
    <div className="space-y-2">
      {filled.map((item) => (
        <div key={item.label} className="flex gap-3 text-sm">
          <span className="text-foreground-400 w-24 shrink-0">{item.label}</span>
          <span className="text-foreground-800 break-words min-w-0">{item.value}</span>
        </div>
      ))}
    </div>
  );
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-background-50 rounded-xl border border-secondary-200/70 p-4">
      <h3 className="text-sm font-semibold text-primary-600 mb-3 flex items-center gap-1.5">
        <i className="ri-file-list-3-line"></i>
        {title}
      </h3>
      {children}
    </section>
  );
}

export default function ConsultationDetail({ row, onClose }: ConsultationDetailProps) {
  if (!row) return null;

  const level = levelOf(row);
  const asRecord = row as unknown as Record<string, unknown>;
  const percent = Math.min(100, Math.round(((row.total_score || 0) / 36) * 100));

  return (
    <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center">
      <button
        type="button"
        aria-label="关闭详情"
        onClick={onClose}
        className="absolute inset-0 bg-foreground-950/45 backdrop-blur-sm cursor-pointer"
      />
      <div className="relative w-full md:max-w-2xl max-h-[92vh] md:max-h-[88vh] bg-background-100 rounded-t-2xl md:rounded-2xl border border-secondary-200 flex flex-col overflow-hidden">
        <div className="shrink-0 px-4 py-3 border-b border-secondary-200/70 bg-background-50 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-900">问诊详情</p>
            <p className="text-[11px] text-foreground-400 truncate">
              {formatTime(row.created_at)} 提交
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:text-primary-600 hover:bg-background-100 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          <section className="bg-background-50 rounded-xl border border-secondary-200/70 p-4">
            <div className="flex items-center justify-between gap-3">
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-xs font-semibold ${level.badgeClass}`}
              >
                <i className="ri-heart-pulse-line"></i>
                {level.label}
              </span>
              <span className="text-[11px] text-foreground-400">
                已评 {row.rated_count ?? 0}/12 项
              </span>
            </div>
            <p className="mt-3 text-3xl font-heading font-semibold text-foreground-950">
              {row.total_score ?? 0}
              <span className="text-sm font-normal text-foreground-400 ml-1">/ 36 分</span>
            </p>
            <div className="mt-2 h-1.5 w-full bg-secondary-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${level.dotClass}`} style={{ width: `${percent}%` }} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="bg-background-100 rounded-lg px-3 py-2">
                <p className="text-[10px] text-foreground-400">中度以上症状</p>
                <p className="text-sm font-semibold text-foreground-800">{row.severe_count ?? 0} 项</p>
              </div>
              <div className="bg-background-100 rounded-lg px-3 py-2">
                <p className="text-[10px] text-foreground-400">联系方式</p>
                <p className="text-sm font-semibold text-foreground-800 break-all">
                  {row.contact || '未填'}
                </p>
              </div>
            </div>
            {row.focus_summary ? (
              <div className="mt-3">
                <p className="text-[11px] text-foreground-400 mb-1.5">重点关注信号</p>
                <div className="flex flex-wrap gap-1.5">
                  {row.focus_summary.split('、').filter(Boolean).map((item) => (
                    <span
                      key={item}
                      className="px-2 py-1 rounded-md bg-primary-50 border border-primary-200/70 text-[11px] text-primary-700"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </section>

          <Block title="基本信息">
            <ValueRows row={row} fields={basicSections.flatMap((section) => section.fields)} />
          </Block>

          <Block title="检查报告">
            <ValueRows row={row} fields={reportSectionList.flatMap((section) => section.fields)} />
          </Block>

          <Block title="病史与用药">
            <ValueRows row={row} fields={historySectionList.flatMap((section) => section.fields)} />
          </Block>

          <Block title="症状自查">
            <div className="space-y-3">
              {symptomCategories.map((category) => (
                <div key={category.title}>
                  <p className="text-[11px] text-foreground-400 mb-1.5">{category.title}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {category.items.map((item) => {
                      const value = typeof asRecord[item.name] === 'string'
                        ? (asRecord[item.name] as string)
                        : '';
                      return (
                        <div
                          key={item.name}
                          className="flex items-center justify-between gap-2 bg-background-100 rounded-lg px-2.5 py-1.5"
                        >
                          <span className="text-xs text-foreground-700 truncate">{item.label}</span>
                          <span
                            className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${symptomBadge(value)}`}
                          >
                            {value ? optionText(item.name, value) : '未评'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Block>

          <Block title="告诉我更多">
            <ValueRows row={row} fields={moreSectionList.flatMap((section) => section.fields)} />
          </Block>
        </div>

        <div className="shrink-0 px-4 py-3 border-t border-secondary-200/70 bg-background-50">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
}